import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  RotateCcw,
} from "lucide-react"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import { Dialog } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ScenarioDetail } from "@/components/domain/scenario-detail"
import type { RehearsalDetail, SuiteSkillBucket, RehearsalRunRow } from "@/types/agent_testing"
import type { TestLabScenario, TestRunStatus } from "@/types/test_lab"

export type Filter = {
  failureOnly: boolean
  search: string
}

type Props = {
  rehearsal: RehearsalDetail
  filter: Filter
  testStream: string
}

// CSRF-aware POST helper — mirrors the one in pages/Testing/Index.tsx so the
// per-row rerun button can hit /skills/:id/test_runs/:run_id/replay without
// reaching back up through page-level state.
async function csrfPost(path: string): Promise<Response> {
  const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
  })
}

type SelectedRun = {
  runId: number
  skillId: number | null
  status: string
  scenarioKey: string
  scenarioName: string
  mode: "isolation" | "integration"
}

export function RehearsalResultsTable({ rehearsal, filter, testStream }: Props) {
  const [selectedRun, setSelectedRun] = useState<SelectedRun | null>(null)
  const [busyRunIds, setBusyRunIds] = useState<Set<number>>(new Set())

  const filteredBuckets = useMemo(
    () => rehearsal.runs_by_skill.map((b) => ({ ...b, runs: applyFilter(b.runs, filter) })),
    [rehearsal.runs_by_skill, filter]
  )

  const visible = filteredBuckets.filter((b) => b.runs.length > 0)

  // Replay a single run. Used by per-row icon buttons and the modal footer.
  // After the POST returns, ask Inertia to refresh latestRehearsal so the
  // row's status flips to running; the cable subscription on Testing/Index
  // will stream subsequent updates.
  async function rerunRun(runId: number, skillId: number | null) {
    if (skillId == null) return
    setBusyRunIds((prev) => new Set(prev).add(runId))
    try {
      const res = await csrfPost(`/skills/${skillId}/test_runs/${runId}/replay`)
      if (!res.ok) {
        console.error("replay failed:", res.statusText)
        return
      }
      router.reload({ only: ["latestRehearsal"] })
    } finally {
      setBusyRunIds((prev) => {
        const next = new Set(prev); next.delete(runId); return next
      })
    }
  }

  function openRun(run: RehearsalRunRow, skillId: number | null) {
    setSelectedRun({
      runId: run.id,
      skillId,
      status: run.status,
      scenarioKey: run.scenario_key,
      scenarioName: run.scenario_name,
      mode: run.mode,
    })
  }

  if (visible.length === 0) {
    return (
      <div className="rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
        No runs match the current filter.
      </div>
    )
  }

  return (
    <>
      <div className="space-y-3">
        {visible.map((bucket) => (
          <SkillBucket
            key={`${bucket.skill_key}-${bucket.skill_id ?? "_"}`}
            bucket={bucket}
            busyRunIds={busyRunIds}
            onSelect={openRun}
            onRerun={rerunRun}
          />
        ))}
      </div>

      <RunDetailDialog
        selected={selectedRun}
        testStream={testStream}
        busyRunIds={busyRunIds}
        onClose={() => setSelectedRun(null)}
        onRerun={rerunRun}
      />
    </>
  )
}

function SkillBucket({
  bucket, busyRunIds, onSelect, onRerun,
}: {
  bucket: SuiteSkillBucket
  busyRunIds: Set<number>
  onSelect: (run: RehearsalRunRow, skillId: number | null) => void
  onRerun: (runId: number, skillId: number | null) => void
}) {
  const allPassing = bucket.passing_count === bucket.total_count
  // Failing buckets default-open; passing buckets default-closed.
  const [open, setOpen] = useState(!allPassing)
  const passRate = bucket.total_count > 0 ? (bucket.passing_count / bucket.total_count) * 100 : 0

  return (
    <div className="rounded-card border border-border bg-app-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition hover:bg-white/5"
        id={bucket.skill_key ? `skill-${bucket.skill_key}` : undefined}
      >
        {open ? <ChevronDown size={14} className="text-app-muted" /> : <ChevronRight size={14} className="text-app-muted" />}
        <span className="font-medium text-app-text">{bucket.skill_name}</span>
        <code className="rounded bg-white/5 px-1 text-[11px] text-app-muted">{bucket.skill_key}</code>

        <div className="ml-auto flex items-center gap-3">
          <div className="h-1.5 w-24 rounded-full bg-app-panel2 overflow-hidden">
            <div
              className={cn("h-full", allPassing ? "bg-status-success" : "bg-status-error")}
              style={{ width: `${passRate}%` }}
            />
          </div>
          <span className={cn("text-xs", allPassing ? "text-status-success" : "text-status-error")}>
            {bucket.passing_count}/{bucket.total_count} passing
          </span>
        </div>
      </button>

      {open && (
        <div className="divide-y divide-border border-t border-border">
          {bucket.runs.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              skillId={bucket.skill_id}
              busy={busyRunIds.has(run.id)}
              onSelect={() => onSelect(run, bucket.skill_id)}
              onRerun={() => onRerun(run.id, bucket.skill_id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function RunRow({
  run, skillId, busy, onSelect, onRerun,
}: {
  run: RehearsalRunRow
  skillId: number | null
  busy: boolean
  onSelect: () => void
  onRerun: () => void
}) {
  const isBad = run.status === "failed" || run.status === "errored"
  const canRerun = skillId != null

  // Row is a div+role=button (not a real <button>) because we need a nested
  // <button> for the rerun control — nested interactive elements are
  // invalid HTML. Keyboard activation is wired via onKeyDown.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onSelect()
        }
      }}
      className={cn(
        "grid w-full cursor-pointer grid-cols-[24px_1fr_auto_auto_auto_auto] items-center gap-3 px-4 py-2.5 text-left text-sm transition hover:bg-white/5 focus:outline-none focus:bg-white/5",
        isBad && "border-l-2 border-l-status-error/50",
      )}
    >
      <StatusIcon status={run.status} />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-app-text">{run.scenario_name || run.scenario_key}</span>
          <ModeChip mode={run.mode} />
        </div>
        <code className="text-[10px] text-app-muted">{run.scenario_key}</code>
      </div>
      <span className={cn("text-[11px] uppercase tracking-wide", statusTone(run.status))}>{run.status}</span>
      <span className="text-[11px] text-app-muted">
        {run.duration_ms != null ? `${(run.duration_ms / 1000).toFixed(1)}s` : "—"}
      </span>
      <span className="text-[11px] text-app-muted">{fmtCompactRelative(run.finished_at)}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          if (canRerun && !busy) onRerun()
        }}
        disabled={!canRerun || busy}
        title={canRerun ? "Rerun this scenario" : "Cannot rerun — no owning skill"}
        className={cn(
          "rounded-control border border-border p-1 text-app-muted transition",
          (canRerun && !busy) && "hover:border-brand-red hover:text-app-text",
          (!canRerun || busy) && "opacity-50",
        )}
      >
        <RotateCcw size={14} className={cn(busy && "animate-spin")} />
      </button>
    </div>
  )
}

function RunDetailDialog({
  selected, testStream, busyRunIds, onClose, onRerun,
}: {
  selected: SelectedRun | null
  testStream: string
  busyRunIds: Set<number>
  onClose: () => void
  onRerun: (runId: number, skillId: number | null) => void
}) {
  if (!selected) {
    return <Dialog open={false} onClose={onClose}>{null}</Dialog>
  }

  const { runId, skillId, status, scenarioKey, scenarioName, mode } = selected
  const isFailureLike = status === "failed" || status === "errored"
  const designerHref = skillId != null ? `/skills/${skillId}/test?tab=design` : null
  const busy = busyRunIds.has(runId)

  // Build a minimal TestLabScenario shim from the rehearsal row + run id.
  // ScenarioDetail fetches the full TestRunDetail from the server itself, so
  // it only really needs the latestRunId and a few header fields. Anything
  // not present in the row defaults to a safe empty value.
  const scenarioShim: TestLabScenario = {
    id: 0,
    key: scenarioKey,
    name: scenarioName,
    goal: null,
    persona: null,
    category: null,
    scope: null,
    mode,
    maxTurns: null,
    turnsCount: 0,
    assertionsCount: 0,
    executable: true,
    skipReason: null,
    allowedToolCalls: [],
    latestRunId: runId,
    latestRunStatus: status as TestRunStatus,
    latestRunAt: null,
    latestRunSummary: null,
    latestRunDurationMs: null,
    latestRunCostUsd: null,
    latestRunVerdicts: [],
  }

  return (
    <Dialog open={!!selected} onClose={onClose} size="lg">
      <div className="flex max-h-[85vh] flex-col">
        {isFailureLike && (
          <div className="rounded-card border border-status-warning/30 bg-status-warning/10 px-3 py-2 text-[11px] text-status-warning">
            AI is non-deterministic. If a failure looks borderline, retry once before treating it as a regression.
          </div>
        )}

        {skillId != null ? (
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <ScenarioDetail
              skillId={skillId}
              scenario={scenarioShim}
              testStream={testStream}
              onFix={() => Promise.resolve()}
            />
          </div>
        ) : (
          <div className="rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
            This run isn't attached to a skill — open the rehearsal directly to investigate.
          </div>
        )}

        <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-border px-6 py-4">
          {designerHref && (
            <a
              href={designerHref}
              className="inline-flex items-center justify-center rounded-control border border-border bg-white/5 px-3 py-1.5 text-xs font-medium text-app-text transition hover:bg-white/10"
            >
              Open in designer
            </a>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onRerun(runId, skillId)}
            disabled={skillId == null || busy}
          >
            <RotateCcw size={14} className={cn("mr-1", busy && "animate-spin")} />
            {busy ? "Rerunning…" : "Rerun"}
          </Button>
          <Button size="sm" variant="red" onClick={onClose}>
            Close
          </Button>
        </footer>
      </div>
    </Dialog>
  )
}

function StatusIcon({ status }: { status: string }) {
  if (status === "passed")  return <CheckCircle2 size={16} className="text-status-success" />
  if (status === "failed")  return <XCircle size={16} className="text-status-error" />
  if (status === "errored") return <AlertCircle size={16} className="text-status-warning" />
  return <Clock size={16} className="text-app-muted" />
}

function ModeChip({ mode }: { mode: "isolation" | "integration" }) {
  const isInt = mode === "integration"
  return (
    <span className={cn(
      "shrink-0 rounded-control border px-1.5 py-0.5 text-[9px] uppercase tracking-wide",
      isInt
        ? "border-sky-500/30 bg-sky-500/10 text-sky-300"
        : "border-app-muted/30 bg-white/5 text-app-muted",
    )}>
      {isInt ? "int" : "iso"}
    </span>
  )
}

function statusTone(status: string): string {
  if (status === "passed")  return "text-status-success"
  if (status === "failed")  return "text-status-error"
  if (status === "errored") return "text-status-warning"
  return "text-app-muted"
}

function applyFilter(runs: RehearsalRunRow[], filter: Filter): RehearsalRunRow[] {
  const q = filter.search.trim().toLowerCase()
  return runs.filter((r) => {
    if (filter.failureOnly && r.status !== "failed") return false
    if (q && !r.scenario_name.toLowerCase().includes(q) && !r.scenario_key.toLowerCase().includes(q)) return false
    return true
  })
}
